import { DOCUMENT, Injectable, inject } from '@angular/core';
import { OAuthService, type OAuthStorage } from 'angular-oauth2-oidc';
import { environment } from '../../../environments/environment';
import { getRedirectUrl } from '../../shared/helpers/url.helpers';

@Injectable({
  providedIn: 'root',
})
export class SsoAuthService {
  private oauthService = inject(OAuthService);

  initialize(): Promise<void> {
    this.configureOauth();
    return this.oauthService
      .loadDiscoveryDocumentAndTryLogin()
      .then(() => undefined)
      .catch((error: unknown) => {
        console.error(error);
      });
  }

  tryLogin(): Promise<boolean> {
    return this.oauthService
      .loadDiscoveryDocumentAndTryLogin()
      .then(() => this.oauthService.hasValidAccessToken());
  }

  startLogin(state: string): void {
    this.oauthService.initCodeFlow(state);
  }

  hasValidAccessToken(): boolean {
    return this.oauthService.hasValidAccessToken();
  }

  getAccessToken(): string {
    return this.oauthService.getAccessToken();
  }

  getPostLoginRedirectPath(): string | null {
    const state = this.oauthService.state;

    if (!state) {
      return null;
    }

    try {
      return decodeURIComponent(state);
    } catch {
      return state;
    }
  }

  logOut(): void {
    this.oauthService.logOut();
  }

  private configureOauth(): void {
    this.oauthService.configure({
      issuer: environment.sso.issuer,
      clientId: environment.sso.clientId,
      redirectUri: getRedirectUrl('/auth/callback'),
      responseType: 'code',
      scope: 'openid profile email offline_access',
      showDebugInformation: !environment.production,
      strictDiscoveryDocumentValidation: false,
      postLogoutRedirectUri: getRedirectUrl(),
    });

    this.oauthService.setupAutomaticSilentRefresh();
  }
}

export const createInMemoryStorage = (): OAuthStorage => {
  const storage = new Map<string, string>();
  return {
    getItem: (key: string) => storage.get(key) ?? null,
    removeItem: (key: string) => {
      storage.delete(key);
    },
    setItem: (key: string, value: string) => {
      storage.set(key, value);
    },
  };
};

export const oAuthStorageFactory = (): OAuthStorage => {
  const document = inject(DOCUMENT, { optional: true });
  return document?.defaultView?.sessionStorage ?? createInMemoryStorage();
};
